import { useContext, useEffect, useRef, useState } from "react";
import { Badge, Button } from "flowbite-react";
import { ResponseContext } from "../../../context/responseProvider";
import { editMerchItem } from "../../../services/merchManager/merchManagerActions";
import MerchFrom from "./merchFrom";
import { useMerchForm } from "../../../hooks/useMerchForm";
import { CustomModal } from "../../../layouts/customModal";
import { Dialog } from "@headlessui/react";
import { useVerifiedUser } from "../../../hooks/useVerifiedUser";

const DEFAULT_FORM = {
  id: null,
  item_name: "",
  item_count: "",
  item_purpose: "",
  item_location: "",
  storage_location: "",
  assets: [],
};

const EditMerchForm = ({ item, handleApi }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { getResponse, setResponse } = useContext(ResponseContext);
  const { isVerified } = useVerifiedUser(["merch_editor"]);

  const formRef = useRef(null);
  const modalRef = useRef(null);
  const openRef = useRef(null);
  const response = getResponse("update_merch");

  const {
    form,
    previews,
    fieldErrors,
    disableBtn,
    fileInputRef,
    handleChange,
    handleFiles,
    removeFile,
    reset,
    setForm,
    setPreviews,
  } = useMerchForm(item, DEFAULT_FORM);

  const handleClose = () => modalRef.current();

  const handleSubmit = (e) => {
    e.preventDefault();
    editMerchItem({
      isVerified,
      form,
      email: localStorage.getItem("userEmail"),
      setResponse,
      setIsLoading,
      formRef,
      onSuccess: () => {
        reset();
        handleApi();
        handleClose();
      },
    });
  };

  useEffect(() => {
    if (!item) return;
    setForm({
      id: item.id,
      item_name: item.item_name ?? "",
      item_count: item.item_count ?? "",
      item_purpose: item.item_purpose ?? "",
      item_location: item.item_location ?? "",
      restock_expected: item.restock_expected ?? false,
      storage_location: item.storage_location ?? "",
      assets: item.assets ?? [],
    });
    setPreviews(
      item.assets?.map((a) => ({
        fileName: a.fileName,
        objectUrl: a.url,
      })) ?? [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  return (
    <CustomModal
      btnText="Edit item"
      modalRef={modalRef}
      openRef={openRef}
      closeOnOutsideClick={true}
      customBtn={
        <button
          onClick={() => openRef.current()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          Update item
        </button>
      }
    >
      <div className="w-full max-h-[80vh] overflow-y-auto sm:w-[480px]">
        <div className="flex h-full flex-col gap-4 p-6 pl-2">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900 inline-flex"
          >
            <strong className="flex align-middle">Edit Item</strong>
          </Dialog.Title>
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <MerchFrom
              form={form}
              handleChange={handleChange}
              fieldErrors={fieldErrors}
              previews={previews}
              fileInputRef={fileInputRef}
              handleFiles={handleFiles}
              removeFile={removeFile}
            />
            {/* ── API error ── */}
            {response?.status === false && (
              <Badge color="failure">{response?.message}</Badge>
            )}

            {/* ── Actions ── */}
            <div className="flex mt-2 items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                className="text-sm font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                onClick={handleClose}
              >
                Cancel
              </button>
              <Button type="submit" disabled={disableBtn || isLoading}>
                {isLoading ? "Updating..." : "Update item"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditMerchForm;
